import type { AxiosInstance } from "axios";
import { Clock, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "../lib/cn.js";
import { fetchStoreJoinStatus, joinBanner, type JoinRequest } from "../lib/join-requests.js";

/**
 * The merchant's line about their request to join an agency (Flow 1): waiting, or not approved —
 * for the store that is open. Nothing once it is approved (the store is then the agency's), and
 * nothing when it could not be read.
 */
export function JoinRequestBanner({
  client,
  tenantId,
  onAskAgain,
}: {
  readonly client: AxiosInstance;
  /** The open store: its request is the one shown, and switching stores re-reads. */
  readonly tenantId: string;
  readonly onAskAgain: () => void;
}): React.JSX.Element | null {
  const [latest, setLatest] = useState<JoinRequest | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLatest(null);
    void fetchStoreJoinStatus(client)
      .then((status) => {
        if (!cancelled) setLatest(status.latest);
      })
      .catch((error: unknown) => {
        void error;
      });
    return () => {
      cancelled = true;
    };
  }, [client, tenantId]);

  const banner = joinBanner(latest);
  if (banner === null) return null;
  const info = banner.tone === "info";

  return (
    <div
      role="status"
      data-testid="join-request-banner"
      className={cn(
        "mx-4 mt-3 mb-1 flex items-center gap-2.5 rounded-lg border px-4 py-2.5 text-sm md:mx-6",
        info
          ? "border-blue-200 bg-blue-50 text-blue-950 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100"
          : "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100",
      )}
    >
      {info ? <Clock aria-hidden className="size-4 shrink-0 text-blue-600" /> : <TriangleAlert aria-hidden className="size-4 shrink-0 text-amber-600" />}
      <p className="min-w-0 flex-1">{banner.text}</p>
      {banner.askAgain && (
        <button type="button" onClick={onAskAgain} className="shrink-0 cursor-pointer font-medium whitespace-nowrap underline underline-offset-4">
          Ask again
        </button>
      )}
    </div>
  );
}
